// useFmt — single hook every UI component calls instead of importing formatCurrency directly.
// It reads displayCurrency + language + fxRates from PrefsContext and returns memoized
// formatters that "just work" — when the user switches currency or language in Settings,
// every dashboard/KPI/table re-renders with the new locale formatting automatically.

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePrefs } from "@/contexts/PrefsContext";
import {
  formatCurrency as rawFormatCurrency,
  formatPercent as rawFormatPercent,
  formatNumber as rawFormatNumber,
} from "@/lib/format";

export function useFmt() {
  const { displayCurrency, fxRates } = usePrefs();
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";

  const money = useCallback(
    (value, opts = {}) => {
      // When the user has chosen a non-USD display currency, show both the USD original
      // and the converted secondary unless `opts.primaryOnly` is true.
      const showOriginal =
        opts.primaryOnly === true ? false : displayCurrency && displayCurrency !== "USD";
      return rawFormatCurrency(value, {
        currency: displayCurrency || "USD",
        lang,
        fxRates,
        cents: !!opts.cents,
        showOriginal,
      });
    },
    [displayCurrency, fxRates, lang]
  );

  const percent = useCallback(
    (value, opts = {}) =>
      rawFormatPercent(value, {
        decimals: opts.decimals ?? 1,
        wholeNumber: !!opts.wholeNumber,
        lang,
      }),
    [lang]
  );

  const number = useCallback(
    (value, decimals = 0) => rawFormatNumber(value, decimals, lang),
    [lang]
  );

  return useMemo(() => ({ money, percent, number, lang, displayCurrency }), [
    money,
    percent,
    number,
    lang,
    displayCurrency,
  ]);
}
